import { useState, useEffect, useCallback } from 'react';
import { UserCircle, Plus, Edit, UserX, UserCheck, Calendar, Shield } from 'lucide-react';
import Card from '../components/common/Card';
import Table from '../components/common/Table';
import SearchBar from '../components/common/SearchBar';
import Pagination from '../components/common/Pagination';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import Button from '../components/common/Button';
import Toast from '../components/common/Toast';
import CreateUserModal from '../components/features/UserManagement/CreateUserModal';
import EditUserModal from '../components/features/UserManagement/EditUserModal';
import ConfirmDisableModal from '../components/features/UserManagement/ConfirmDisableModal';
import { getUsers, enableUser } from '../services/userService';
import './Users.css';

/**
 * Users Page Component (Admin Only)
 * 
 * Displays list of users with search, filter, and management actions
 */
export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Toast notification state
  const [toast, setToast] = useState(null);

  // Fetch users function (defined before useEffect)
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        search: searchTerm,
        role: roleFilter,
        status: statusFilter,
        page: currentPage,
        limit: itemsPerPage
      };

      const response = await getUsers(params);
      setUsers(response.data);
      setTotalPages(response.pagination.totalPages);
    } catch (err) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, roleFilter, statusFilter, currentPage]);

  // Fetch users on mount and when dependencies change
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Handle search
  const handleSearch = (e) => {
    const value = typeof e === 'string' ? e : e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
  };

  // Handle role filter
  const handleRoleChange = (e) => {
    setRoleFilter(e.target.value);
    setCurrentPage(1);
  };

  // Handle status filter
  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  // Get role badge
  const getRoleBadge = (role) => {
    const roleConfig = {
      admin: { label: 'Admin', className: 'role-admin' },
      manager: { label: 'Report Manager', className: 'role-manager' },
      viewer: { label: 'Viewer', className: 'role-viewer' }
    };

    const config = roleConfig[role] || { label: role, className: 'role-default' };

    return (
      <span className={`role-badge ${config.className}`}>
        <Shield size={14} />
        {config.label}
      </span>
    );
  };

  // Get status badge
  const getStatusBadge = (status) => {
    if (status === 'active') {
      return (
        <span className="status-badge status-active">
          <UserCheck size={14} />
          Active
        </span>
      );
    } else {
      return (
        <span className="status-badge status-disabled">
          <UserX size={14} />
          Disabled
        </span>
      );
    }
  };

  // Table columns
  const columns = [
    {
      key: 'name',
      label: 'Name',
      sortable: false,
      render: (user) => (
        <div className="user-name-cell">
          <UserCircle size={18} />
          <span>{user.name}</span>
        </div>
      )
    },
    {
      key: 'email',
      label: 'Email',
      sortable: false
    },
    {
      key: 'role',
      label: 'Role',
      sortable: false,
      render: (user) => getRoleBadge(user.role)
    },
    {
      key: 'status',
      label: 'Status',
      sortable: false,
      render: (user) => getStatusBadge(user.status)
    },
    {
      key: 'lastLogin',
      label: 'Last Updated',
      sortable: false,
      render: (user) => (
        <div className="date-cell">
          <Calendar size={16} />
          <span>{new Date(user.lastLogin).toLocaleDateString()}</span>
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (user) => (
        <div className="action-buttons">
          <button
            className="action-btn edit-btn"
            onClick={() => handleEditUser(user)}
            title="Edit user"
          >
            <Edit size={16} />
          </button>
          {user.status === 'active' ? (
            <button
              className="action-btn disable-btn"
              onClick={() => handleDisableUser(user)}
              title="Disable user"
            >
              <UserX size={16} />
            </button>
          ) : (
            <button
              className="action-btn enable-btn"
              onClick={() => handleEnableUser(user)}
              title="Enable user"
            >
              <UserCheck size={16} />
            </button>
          )}
        </div>
      )
    }
  ];

  // Action handlers (placeholders for now)
  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleDisableUser = (user) => {
    setSelectedUser(user);
    setShowDisableModal(true);
  };

  const handleEnableUser = async (user) => {
    try {
      await enableUser(user.id);
      showToast('User enabled successfully', 'success');
      fetchUsers();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to enable user', 'error');
    }
  };

  const handleCreateUser = () => {
    setShowCreateModal(true);
  };

  // Toast helper
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  // Handle successful operations
  const handleSuccess = (message) => {
    showToast(message, 'success');
    // Refresh user list
    fetchUsers();
  };

  return (
    <div className="users-page">
      <div className="page-header">
        <div className="header-content">
          <div className="header-text">
            <h1 className="page-title">
              <UserCircle size={32} />
              User Management
            </h1>
            <p className="page-subtitle">Manage user accounts and permissions</p>
          </div>
          <Button
            variant="primary"
            onClick={handleCreateUser}
            icon={<Plus size={20} />}
          >
            Create User
          </Button>
        </div>
      </div>

      <Card className="users-card">
        {/* Search and Filter Bar */}
        <div className="users-controls">
          <SearchBar
            value={searchTerm}
            onChange={handleSearch}
            placeholder="Search users by name or email..."
          />
          
          <div className="filter-controls">
            <select
              className="role-filter"
              value={roleFilter}
              onChange={handleRoleChange}
            >
              <option value="">All Roles</option>
              <option value="admin">Admin</option>
              <option value="manager">Report Manager</option>
              <option value="viewer">Viewer</option>
            </select>

            <select
              className="status-filter"
              value={statusFilter}
              onChange={handleStatusChange}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>
        </div>

        {/* Loading State */}
        {loading && <LoadingSpinner />}

        {/* Error State */}
        {error && (
          <ErrorMessage
            message={error}
            onRetry={() => window.location.reload()}
          />
        )}

        {/* Users Table */}
        {!loading && !error && (
          <>
            <Table
              columns={columns}
              data={users}
              emptyMessage="No users found"
            />

            {/* Pagination */}
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}
      </Card>

      {/* Modals */}
      <CreateUserModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleSuccess}
      />

      <EditUserModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onSuccess={handleSuccess}
      />

      <ConfirmDisableModal
        isOpen={showDisableModal}
        onClose={() => {
          setShowDisableModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onSuccess={handleSuccess}
      />

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={5000}
        />
      )}
    </div>
  );
}
